import type { GeoJSONSource, Map as MLMap } from 'maplibre-gl';
import type { LngLat, Route } from '../shared/types';

const SRC = 'route';
/** ルートを選ぶ間だけ出す、選んでいない候補 */
const ALT_SRC = 'route-alt';
const LAYERS = ['route-alt-line', 'route-casing', 'route-line'];

/** ルート線の描画（MapLibre 標準レイヤーのみ。Three.js 不要） */
export class RouteLayer {
  /** 経路の出典（ORS など）。地図の帰属表示に出す */
  private attribution: string | undefined;
  private route: Route | null = null;
  private alternatives: Route[] = [];

  constructor(private readonly map: MLMap) {}

  /** スタイル読み込み後に1回呼ぶ */
  install(): void {
    this.map.addSource(ALT_SRC, { type: 'geojson', data: lines(this.alternatives) });
    this.map.addSource(SRC, { type: 'geojson', data: lines(this.route ? [this.route] : []), attribution: this.attribution });
    // 作り直したときも 3D の自車（カスタムレイヤー）より下に描く
    const before = this.map.getLayersOrder().find((id) => this.map.getLayer(id)?.type === 'custom');
    this.map.addLayer({
      id: 'route-alt-line',
      type: 'line',
      source: ALT_SRC,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#7d8799',
        'line-opacity': 0.85,
        'line-width': ['interpolate', ['linear'], ['zoom'], 10, 4, 18, 12],
      },
    }, before);
    this.map.addLayer({
      id: 'route-casing',
      type: 'line',
      source: SRC,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#0b3d91', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 6, 18, 20] },
    }, before);
    this.map.addLayer({
      id: 'route-line',
      type: 'line',
      source: SRC,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#3d8bff', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 3, 18, 13] },
    }, before);
  }

  /** alternatives: ルートを選んでいる間の、選ばれていない候補（灰色で描く） */
  set(route: Route | null, attribution?: string, alternatives: Route[] = []): void {
    this.route = route;
    this.alternatives = alternatives;
    const next = route ? attribution : undefined;
    // ソースの出典は後から変えられないので、変わったときだけ作り直す
    if (next !== this.attribution && this.map.getSource(SRC)) {
      this.attribution = next;
      for (const id of LAYERS) this.map.removeLayer(id);
      this.map.removeSource(SRC);
      this.map.removeSource(ALT_SRC);
      this.install();
      return;
    }
    this.map.getSource<GeoJSONSource>(SRC)?.setData(lines(route ? [route] : []));
    this.map.getSource<GeoJSONSource>(ALT_SRC)?.setData(lines(alternatives));
  }
}

function lines(routes: Route[]) {
  return {
    type: 'FeatureCollection' as const,
    features: routes.map((r) => ({
      type: 'Feature' as const,
      properties: {},
      geometry: { type: 'LineString' as const, coordinates: r.geometry as LngLat[] },
    })),
  };
}
